import { countStatuses } from './scoring.js';
/** Where a trend's counting changed: the first quarter that counts partly in place, when earlier quarters did not. */
export function partlyCountedFrom(points) {
    const first = points.find((p) => p.countsPartly);
    return first && points.some((p) => !p.countsPartly && p.quarter < first.quarter) ? first.quarter : null;
}
/** DEC-40: how a finding linked by family (or of unknown link origin) is labelled, on the page and in the PDF. */
export const FAMILY_LINK_LABEL = 'Matched by finding type, for National IT to check';
/**
 * DEC-40: link sources that are not yet reviewed. 'unknown' (links that predate migration 0028) is assumed to be the
 * same as 'family' here: never confirmed, labelled to review (assumption logged for Andres, 5C NEXT PASS 2).
 */
export const LINK_SOURCES_TO_REVIEW = ['family', 'unknown'];
/**
 * How a CDW finding is tied to a control, said accurately for each link source (migration 0028), for the assistant's
 * tools and anywhere a link is described: never "reviewed" for a link nobody reviewed.
 */
export const LINK_SOURCE_LABELS = {
    description: 'Linked by its reviewed description',
    family: FAMILY_LINK_LABEL,
    unknown: FAMILY_LINK_LABEL,
    manual: 'Linked by hand',
    seed: 'Synthetic demo link',
};
/** The SQL CASE that says how a link was made, from LINK_SOURCE_LABELS (constants only; no input reaches it). */
export const linkSourceLabelSql = (column) => `CASE ${column} ${Object.entries(LINK_SOURCE_LABELS).map(([k, v]) => `WHEN N'${k}' THEN N'${v.replace(/'/g, "''")}'`).join(' ')} ELSE N'${FAMILY_LINK_LABEL}' END`;
export const linksToReview = (sources) => sources.some((s) => s !== null && s !== undefined && LINK_SOURCES_TO_REVIEW.includes(s));
/**
 * The six steps of the CDW assessment program, as the firm dashboard shows them: the five tracker milestones,
 * then roadmapping (the session that turns the report into action plans).
 */
export const PROGRAM_STEPS = ['kickoff', 'pen_test', 'pt_report', 'risk_validation', 'ras_report', 'roadmap'];
export const PROGRAM_STEP_LABEL = {
    kickoff: 'Kickoff',
    pen_test: 'Penetration test',
    pt_report: 'Pen test report',
    risk_validation: 'Risk validation meeting',
    ras_report: 'Risk assessment report',
    roadmap: 'Roadmapping',
};
/**
 * The same steps in plain words for Leadership (F1, NEXT PASS 1); the tracker's own names (PROGRAM_STEP_LABEL) stay
 * one click down, for the IT reader who knows them.
 */
export const PROGRAM_STEP_PLAIN = {
    kickoff: 'Start meeting',
    pen_test: 'Break-in test',
    pt_report: 'Break-in test report',
    risk_validation: 'Meeting to confirm the risks',
    ras_report: 'Risk report',
    roadmap: 'Plan of next steps',
};
/**
 * The tracker's next-step text (the API's `nextStep`, e.g. "Penetration test: under way" or "Roadmapping under way,
 * next session October 6, 2026") in the plain step names, through PROGRAM_STEP_PLAIN, so the network table says what
 * the firm card says (NEXT PASS 2, item 3). Text that names no tracker step ("National schedules the assessment with
 * the firm") is returned as is. The tracker's own names stay one click down, in the glossary beside each use.
 */
export function plainNextStep(text) {
    if (!text)
        return text;
    // The tracker's line once every step is done ("Action plans agreed; remediation under way"), in the same plain words.
    if (text === 'Action plans agreed; remediation under way')
        return `${PROGRAM_STEP_PLAIN.roadmap} done; the firm is acting on it`;
    const hit = Object.keys(PROGRAM_STEP_LABEL)
        .sort((a, b) => PROGRAM_STEP_LABEL[b].length - PROGRAM_STEP_LABEL[a].length)
        .find((k) => text.startsWith(PROGRAM_STEP_LABEL[k]));
    if (!hit)
        return text;
    let rest = text.slice(PROGRAM_STEP_LABEL[hit].length);
    if (/^: (under way|planned|not started)/.test(rest))
        rest = rest.slice(1);
    else if (/^ session/.test(rest))
        rest = `:${rest}`;
    return `${PROGRAM_STEP_PLAIN[hit]}${rest}`;
}
/** The CDW cycle every current status and higher risk is read from. */
export const LEADERSHIP_CYCLE = 'CDW-2026';
/**
 * The year of a cycle id ("CDW-2026" to 2026). SFH-09 (5E pre-gate): a cycle id in any other form stops the module
 * from loading, rather than naming the program "Risk Assessment NaN".
 */
export function cycleYear(cycle) {
    const m = /^CDW-(\d{4})$/.exec(cycle);
    if (!m)
        throw new Error(`The assessment cycle id "${cycle}" is not in the form CDW-YYYY`);
    return Number(m[1]);
}
/**
 * The current assessment cycle's year, read from LEADERSHIP_CYCLE (the 5E PLAN's assumption): the program's name
 * follows the cycle, not the calendar, so it changes when a new cycle is set.
 */
export const ASSESSMENT_CYCLE_YEAR = cycleYear(LEADERSHIP_CYCLE);
/** The assessment program's name wherever it is named: the tab, the headings and the PDF (DEC-58 item 5, superseding DEC-49's name). */
export const PROGRAM_NAME = `Risk Assessment ${ASSESSMENT_CYCLE_YEAR}`;
/** The first day an assessment activity can count from: the start of the current CDW cycle. */
export const CDW_CYCLE_START = `${ASSESSMENT_CYCLE_YEAR}-01-01`;
/**
 * The start date rule. A firm's CDW assessment started on the earliest milestone completed with a date in 2026
 * (usually the penetration test; a 2026 kickoff counts too). A later milestone done or any milestone under way
 * without a date still means started, date not recorded. Otherwise it is planned for the earliest date (or month)
 * the tracker gives for a milestone still to come, or to be confirmed. A firm with no tracker row that is not in
 * the CDW programme says so.
 */
export function cdwStartOf(milestones, inProgramme) {
    const tracker = milestones.filter((m) => (m.step ?? m.milestone) !== 'roadmap');
    if (!tracker.length)
        return inProgramme ? { status: 'to_be_confirmed', date: null, dateText: null } : { status: 'not_in_programme', date: null, dateText: null };
    const done2026 = tracker.filter((m) => m.status === 'complete' && m.date && m.date >= CDW_CYCLE_START).map((m) => m.date).sort();
    if (done2026.length)
        return { status: 'started', date: done2026[0], dateText: null };
    const kickoff = (m) => (m.step ?? m.milestone) === 'kickoff';
    // A kickoff marked done without a 2026 date is the late-2025 kickoff meeting, not assessment activity.
    if (tracker.some((m) => m.status === 'in_progress' || (m.status === 'complete' && !kickoff(m))))
        return { status: 'started', date: null, dateText: null };
    const ahead = tracker.filter((m) => m.status !== 'complete' && m.status !== 'tbd');
    const dated = ahead.filter((m) => m.date).map((m) => m.date).sort();
    if (dated.length)
        return { status: 'planned', date: dated[0], dateText: null };
    const text = ahead.find((m) => m.dateText)?.dateText;
    return text ? { status: 'planned', date: null, dateText: longDateText(text) } : { status: 'to_be_confirmed', date: null, dateText: null };
}
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const monthOf = (name) => MONTHS.findIndex((m) => name.length >= 3 && m.toLowerCase().startsWith(name.toLowerCase()));
/**
 * One date form across the portal (DEC-23, "September 24, 2026"). The tracker keeps some dates as text ("Oct 6,
 * 2026", "Nov 2026"); these are spelled out the same way. Anything else ("Late 2025") is kept as written.
 */
export function longDateText(text) {
    if (!text)
        return text;
    const t = text.trim();
    const day = /^([A-Za-z]{3,9})\.? (\d{1,2}),? (\d{4})$/.exec(t);
    if (day && monthOf(day[1]) >= 0) {
        const [m, d, y] = [monthOf(day[1]), Number(day[2]), Number(day[3])];
        // Only a real calendar date is respelled; "Feb 30, 2026" stays exactly as the tracker wrote it (SFH-03).
        const real = new Date(Date.UTC(y, m, d));
        return real.getUTCMonth() === m && real.getUTCDate() === d ? `${MONTHS[m]} ${d}, ${y}` : text;
    }
    const month = /^([A-Za-z]{3,9})\.? (\d{4})$/.exec(t);
    if (month && monthOf(month[1]) >= 0)
        return `${MONTHS[monthOf(month[1])]} ${month[2]}`;
    return t;
}
/** A calendar date ("2026-02-10") in the portal's one date form: "February 10, 2026". */
export const longDate = (iso) => {
    const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
    return `${MONTHS[(m ?? 1) - 1]} ${d}, ${y}`;
};
/** The six steps from the tracker's five milestones and the roadmap status (none recorded yet: to be confirmed). */
export function programSteps(milestones, roadmapStatus, roadmapNext = null) {
    const steps = PROGRAM_STEPS.filter((s) => s !== 'roadmap').map((step) => {
        const m = milestones.find((x) => x.milestone === step);
        return { step, status: (m?.status ?? 'tbd'), date: m?.date ?? null, dateText: longDateText(m?.dateText ?? null) };
    });
    const roadmap = roadmapStatus === 'complete' || roadmapStatus === 'in_progress' || roadmapStatus === 'not_started' ? roadmapStatus : 'tbd';
    return [...steps, { step: 'roadmap', status: roadmap, date: null, dateText: roadmap === 'in_progress' && roadmapNext ? `next session ${longDateText(roadmapNext)}` : null }];
}
/** Steps done, under way and still to come (planned or to be confirmed), of the six. */
export function stepCounts(steps) {
    const done = steps.filter((s) => s.status === 'complete').length;
    const underWay = steps.filter((s) => s.status === 'in_progress').length;
    return { done, underWay, planned: steps.length - done - underWay, total: steps.length };
}
export function programSummary(firms, asOf) {
    const at = (s) => firms.filter((f) => f.stage === s).length;
    const kickoffs = firms
        .flatMap((f) => f.steps.filter((s) => s.step === 'kickoff' && s.status !== 'complete' && s.date && s.date >= asOf).map((s) => s.date))
        .sort();
    return {
        firms: firms.length,
        complete: at('complete'),
        underWay: at('in_progress'),
        scheduled: at('scheduled'),
        toBeConfirmed: at('scheduling_tbd'),
        notScheduled: firms.filter((f) => f.stage === null && f.inProgramme).length,
        notInProgramme: firms.filter((f) => f.stage === null && !f.inProgramme).length,
        nextKickoffs: [...new Set(kickoffs)].slice(0, 3),
    };
}
const monthDay = (iso) => new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', timeZone: 'UTC' }).replace('.', '');
/** "8 of 21 firms have completed the CDW assessment; 3 under way; next kickoff Oct 19." */
export function programSummaryLine(s) {
    const parts = [`${s.complete} of ${s.firms} firms have completed the CDW assessment`, `${s.underWay} under way`];
    if (s.nextKickoffs.length)
        parts.push(`next ${s.nextKickoffs.length === 1 ? 'kickoff' : 'kickoffs'} ${s.nextKickoffs.slice(0, 2).map(monthDay).join(' and ')}`);
    return `${parts.join('; ')}.`;
}
/** The firm-by-firm comparison: every active member firm, the CIS areas, and the CDW-only medians. */
/**
 * A firm's BTC Baseline standing from its comparison row: the one rule behind every BTC-by-firm chart (Leadership's
 * "Where firms stand" and the National view's BTC Baseline by firm), so the two cannot differ (N2). CDW results and
 * the firm's own 2026 rating carry their partly share; a firm with no 2026 rating shows its March 2025 survey figure
 * (no partly split, possibly no figure when the survey covered no BTC control); a firm with neither is 'none'.
 */
export function standingOf(r) {
    const who = { firmCode: r.firmCode, firmName: r.firmName };
    if (r.source === 'cdw' || r.source === 'self')
        return { ...who, source: r.source, pct: r.btcPct, partlyPct: r.btcPct === null ? null : (r.btcPartlyPct ?? 0) };
    if (r.march2025)
        return { ...who, source: 'survey', pct: r.march2025.btc.pct, partlyPct: null };
    return { ...who, source: 'none', pct: null, partlyPct: null };
}
export const NETWORK_REQUIREMENT = {
    satisfied: { label: 'Satisfied', tone: 'in_place' },
    partly_satisfied: { label: 'Partly satisfied', tone: 'progress' },
    not_met: { label: 'Not met', tone: 'neutral' },
};
export function networkRequirementState(firms) {
    const positioned = firms.filter((f) => f.status === 'fully' || f.status === 'partly' || f.status === 'not_yet');
    if (positioned.length === 0)
        return null;
    const inPlace = positioned.filter((f) => f.status === 'fully' || f.status === 'partly').length;
    if (inPlace === positioned.length)
        return 'satisfied';
    if (inPlace === 0)
        return 'not_met';
    return 'partly_satisfied';
}
/**
 * The requirement bar: CDW 2026 assessments, and March 2025 survey answers where a firm has no 2026 rating.
 * A firm's own rating and a National IT review stay in the firm list, marked, and are not drawn on the bar.
 */
export function requirementBar(firms) {
    const counted = firms.filter((f) => (f.source === 'cdw' || f.source === 'survey') && (f.status === 'fully' || f.status === 'partly' || f.status === 'not_yet'));
    const fully = counted.filter((f) => f.status === 'fully').length;
    const partly = counted.filter((f) => f.status === 'partly').length;
    const notYet = counted.filter((f) => f.status === 'not_yet').length;
    return { fully, partly, notYet, rated: counted.length };
}
/** March 2025 survey answer as a BTI position. Achieved is fully in place; the survey has no partly. */
export function surveyAsBtiStatus(status) {
    if (status === 'achieved')
        return 'fully';
    if (status === 'not_achieved')
        return 'not_yet';
    return null;
}
/** A control's current status as a BTI position (BtiFirmStatus). */
export function btiFirmStatus(gapStatus) {
    if (gapStatus === 'no_gap')
        return 'fully';
    if (gapStatus === 'partial_gap')
        return 'partly';
    if (gapStatus === 'gap')
        return 'not_yet';
    if (gapStatus === 'out_of_scope')
        return 'out_of_scope';
    return 'not_assessed';
}
/** How each firm position reads, on the page and in the PDF. */
export const BTI_FIRM_STATUS = {
    fully: { label: 'Fully in place', tone: 'in_place' },
    partly: { label: 'Partly in place', tone: 'progress' },
    not_yet: { label: 'Not yet in place', tone: 'neutral' },
    // PL-16: a firm's own missing rating, distinct from BTI's own "Not assessed".
    not_assessed: { label: 'No 2026 rating yet', tone: 'neutral' },
    out_of_scope: { label: 'Out of scope at this firm', tone: 'neutral' },
};
/**
 * 5D (F5, DEC-49): the network's quarterly chart is shown only once this many quarters are stored
 * (core.network_snapshot with a BTC median); below that it is hidden, and shows by itself when the fourth arrives.
 */
export const NETWORK_TREND_MIN_QUARTERS = 4;
/** Stored network quarters with a BTC median. */
export const storedNetworkQuarters = (n) => n.trend.filter((t) => t.btcMedian !== null && t.btcMedian !== undefined).length;
/** Whether the network's quarterly chart is shown (page and PDF): four or more stored quarters. */
export const showsNetworkTrend = (n) => storedNetworkQuarters(n) >= NETWORK_TREND_MIN_QUARTERS;
/**
 * A firm member's own dot that is not a CDW result (the API marks its source, DEC-30): shown apart, never counted
 * or ranked among the firms with CDW results. One rule for the page and the PDF (N3).
 */
export const isApartDot = (d) => Boolean(d.isYou && d.source !== undefined && d.source !== 'cdw');
/** The dots counted and ranked (firms with CDW results), and the member's own non-CDW dot said apart, if any. */
export function rankedDots(distribution) {
    return { ranked: distribution.filter((d) => !isApartDot(d)), apart: distribution.filter(isApartDot) };
}
/** How an apart dot's source reads in a sentence: "self-assessed", else "not from CDW". */
export const apartSourceText = (d) => (d.source === 'self' ? 'self-assessed' : 'not from CDW');
/** Top business risks in plain words, each a group of controls (draft, for review). */
export const BUSINESS_RISKS = [
    { id: 'ransomware', label: 'Ransomware', controls: ['11.2', '11.3', '11.4', '7.3', '7.4', '6.3', '6.5', '10.1', '4.1', 'E8-BACKUPS', 'E8-PATCH-OS', 'E8-PATCH-APPS', 'E8-OFFICE-MACROS'] },
    { id: 'client-data', label: 'Client data exposure', controls: ['3.1', '3.3', '3.6', '6.1', '6.2', '6.3', '5.3', '14.4', '14.5'] },
    { id: 'email-fraud', label: 'Email and payment fraud', controls: ['9.2', '6.3', '14.2', '14.6', '10.1', 'E8-MFA'] },
    { id: 'system-loss', label: 'Losing key systems', controls: ['11.1', '11.2', '11.4', '17.1', '17.2', '17.3', '1.1', '2.2'] },
];
/**
 * What each business risk would mean, in one plain sentence for partners and the Board (5C; draft, reviewed with
 * BUSINESS_RISKS). The consequence, never a verdict on the firm.
 */
export const BUSINESS_RISK_CONSEQUENCE = {
    ransomware: 'Files and systems are locked by criminals and client work stops until they are restored.',
    'client-data': 'Client information is seen or taken by someone who should not have it.',
    'email-fraud': 'A fake or taken-over email tricks staff or clients into sending money or information.',
    'system-loss': 'Key systems are down for days after an outage or attack, with no tested way back.',
};
/**
 * The network's goal for the BTC Baseline (DEC-36): every control in place, fully or partly, by December 31, 2027,
 * with fully in place shown beside it. BTI's own goal stays Wave 2 by BTI_WAVE2_DUE.
 */
export const BTC_BASELINE_GOAL = { pct: 100, by: '2027-12-31' };
/** DEC-37: critical protections share the BTC Baseline's date; plans have no date, every committed plan on track. */
export const CRITICAL_GOAL = { pct: 100, by: BTC_BASELINE_GOAL.by };
/** Each headline measure's goal in words, the same on the page and the PDF (DEC-36, DEC-37). */
export const GOALS = {
    btc: `${BTC_BASELINE_GOAL.pct}% by ${longDate(BTC_BASELINE_GOAL.by)}`,
    critical: `${CRITICAL_GOAL.pct}% by ${longDate(CRITICAL_GOAL.by)}`,
    plans: 'Every committed plan on track',
};
/** How the network's earlier figure is labelled wherever it is shown (DEC-37). */
export const MARCH_2025_NETWORK_LABEL = "March 2025 survey (firms' own answers)";
/** A finding still to fix: not complete, and not accepted by the firm's leaders. */
export const isOpenFinding = (f) => f.status !== 'complete' && f.status !== 'accepted';
/** One rule for "has an owner and a due date", for the count and for the row (SFH-03). */
export const hasOwnerAndDue = (f) => Boolean(f.ownerRole && f.due);
/**
 * The counts said about a findings list. Repeats count only findings known to be repeats; repeatUnknown counts the
 * open ones that cannot be checked (SFH-05); unlinked counts open ones not linked to the baseline controls (SFH-01).
 */
export function findingCounts(list) {
    const open = list.filter(isOpenFinding);
    const closed = list.filter((f) => f.status === 'complete');
    return {
        total: list.length,
        open: open.length,
        owned: open.filter(hasOwnerAndDue).length,
        repeat: open.filter((f) => f.repeat === true).length,
        repeatUnknown: open.filter((f) => f.repeat === null).length,
        unlinked: open.filter((f) => !f.linked).length,
        byFamily: open.filter((f) => f.linked && f.linkedByFamily).length,
        closed: closed.length,
        confirmed: closed.filter((f) => f.progressBy === 'national').length,
        accepted: list.filter((f) => f.status === 'accepted').length,
    };
}
/**
 * The "Higher risks open" tile's rule over findings (SFH-07): rated high or very high, and not accepted by the firm's
 * leaders (HIGHER_RISKS_OPEN_WHERE). A higher risk the firm marked fixed still counts until a response is agreed.
 */
export const higherRisksWithoutAgreedResponse = (list) => list.filter((f) => f.higher && f.status !== 'accepted').length;
/**
 * Open findings and the firms they are at, by business risk id; 'other' for linked findings outside the four
 * (SFH-08) and 'unlinked' for findings not linked to the baseline controls (SFH-01), so the groups add up.
 */
export function openByBusinessRisk(list) {
    const out = {};
    const group = (f) => (!f.linked ? 'unlinked' : (f.riskId ?? 'other'));
    for (const id of [...BUSINESS_RISKS.map((b) => b.id), 'other', 'unlinked']) {
        const open = list.filter((f) => isOpenFinding(f) && group(f) === id);
        out[id] = { open: open.length, firms: new Set(open.map((f) => f.firmCode)).size };
    }
    return out;
}
/**
 * The findings list in reading order: open ones by due date (earliest first), then open ones with no due date,
 * then accepted, then closed (latest first).
 */
export function findingsInOrder(list) {
    // NEXT PASS 5 (F4, DEC-55 item 6): the open findings only. With no status on a row, an accepted or closed finding
    // listed under "N open findings" would read as open; firms see those on their own pages.
    const open = list.filter(isOpenFinding);
    return [
        ...open.filter((f) => f.due).sort((a, b) => a.due.localeCompare(b.due) || a.firmName.localeCompare(b.firmName)),
        ...open.filter((f) => !f.due).sort((a, b) => Number(b.higher) - Number(a.higher) || a.firmName.localeCompare(b.firmName)),
    ];
}
/**
 * What Canadian cyber insurance applications commonly ask, mapped to our controls (draft, for review).
 * Status: all mapped controls in place = in place; some = partly; none = not yet; none rated = not rated.
 */
export const INSURANCE_ESSENTIALS = [
    { id: 'mfa', label: 'Multi-factor sign-in', controls: ['6.3', '6.4', '6.5'] },
    { id: 'backups', label: 'Offline, tested backups', controls: ['11.2', '11.3', '11.4'] },
    { id: 'endpoint', label: 'Endpoint protection', controls: ['10.1', '10.2'] },
    { id: 'patching', label: 'Timely patching', controls: ['7.3', '7.4'] },
    { id: 'training', label: 'Phishing training', controls: ['14.1', '14.2'] },
    { id: 'admin', label: 'Limited admin rights', controls: ['5.4'] },
    { id: 'email', label: 'Email filtering', controls: ['9.2'] },
    { id: 'encryption', label: 'Laptop encryption', controls: ['3.6'] },
    { id: 'incident', label: 'Incident response plan', controls: ['17.1', '17.2', '17.3'] },
];
export const LEADERSHIP_CONTENT_STATUS = 'draft';
/**
 * Coverage of a group from each control's current status. inPlace counts controls fully or partly in place, as
 * every baseline does (business risk coverage uses it). The status chip keeps its finer reading for the
 * insurance list: in place only when every mapped control is fully in place, partly when some are in place
 * (fully or partly), not yet when none are.
 */
export function coverageOf(controls, statusOf) {
    const c = countStatuses(controls.map(statusOf));
    const status = c.assessed === 0 ? 'not_rated' : c.fully === controls.length ? 'in_place' : c.inPlace > 0 ? 'partly' : 'not_yet';
    return { status, inPlace: c.inPlace, fully: c.fully, partly: c.partly, rated: c.assessed, total: controls.length };
}
/** "2026-Q3" for a date. Snapshots are taken per calendar quarter. */
export function quarterOf(d) {
    return `${d.getUTCFullYear()}-Q${Math.floor(d.getUTCMonth() / 3) + 1}`;
}
//# sourceMappingURL=leadership.js.map